import type { ReactNode } from 'react';

import { IconButton, Tab, Tabs, Tooltip } from '@mui/material';
import { SettingsIcon } from '@truvhq/ui';
import { useHistory } from 'react-router-dom';

import { NotificationEvent } from 'features/notifications/lib/constants';
import { useNotificationFeed } from 'features/notifications/lib/NotificationFeedContext';
import type { ControlCenterTab } from 'features/onboarding/lib/useOnboardingDialogStore';
import { useOnboardingDialogStore } from 'features/onboarding/lib/useOnboardingDialogStore';
import { trackEvent } from 'shared/lib/analytics/trackEvent';
import { urls } from 'shared/urls';

import CheckDoubleIcon from './icons/check-double.svg?react';
import { EDGE_SAFE_TOOLTIP } from './tooltipSlotProps';

const ICON_BUTTON_CLASS = 'h-8 w-8 p-0 text-grey60';
const TAB_CLASS = 'min-h-12 min-w-0';

/** Header buttons for the feed: mark all read on the Notifications tab, and the settings page. */
export function ControlCenterActions({ tab }: { tab: ControlCenterTab }) {
  const history = useHistory();
  const { feedClient, unreadCount } = useNotificationFeed();

  const markAllAsRead = () => {
    trackEvent(NotificationEvent.MARK_ALL_READ_CLICK);
    feedClient.markAllAsRead();
  };

  const openSettings = () => {
    trackEvent(NotificationEvent.SETTINGS_CLICK);
    history.push(urls.notifications.url());
  };

  return (
    <div className="flex flex-none items-center gap-1">
      {tab === 'notifications' && (
        <Tooltip title="Mark all as read" placement="bottom" slotProps={EDGE_SAFE_TOOLTIP}>
          {/* A disabled button fires no events, so the tooltip listens on a wrapper instead. */}
          <span>
            <IconButton
              aria-label="Mark all as read"
              disabled={unreadCount === 0}
              onClick={markAllAsRead}
              className={ICON_BUTTON_CLASS}
            >
              <CheckDoubleIcon width={20} height={20} />
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Tooltip title="Notification settings" placement="bottom" slotProps={EDGE_SAFE_TOOLTIP}>
        <IconButton aria-label="Notification settings" onClick={openSettings} className={ICON_BUTTON_CLASS}>
          <SettingsIcon width={20} height={20} />
        </IconButton>
      </Tooltip>
    </div>
  );
}

type ControlCenterTabsProps = {
  tab: ControlCenterTab;
  /** The assistant tab reads as the checklist while the steps cover its chat */
  isChecklistOpen: boolean;
  unreadCount: number;
  /** For a phone, where the sheet's own title row leaves the header actions nowhere else to go */
  actions?: ReactNode;
};

export function ControlCenterTabs({ tab, isChecklistOpen, unreadCount, actions }: ControlCenterTabsProps) {
  const selectTab = useOnboardingDialogStore((state) => state.selectTab);
  const setExpanded = useOnboardingDialogStore((state) => state.setExpanded);

  const handleChange = (_: React.SyntheticEvent, next: ControlCenterTab) => {
    trackEvent(NotificationEvent.TAB_CLICK, { tab: next, unread_count: unreadCount });
    selectTab(next);
    // Choosing Notifications is choosing to read them, not to land on the chat a question folded
    // the feed away for. The assistant tab keeps its state: expanding it would rename it under the
    // cursor, from "AI assistant" to "Setup checklist".
    if (next === 'notifications') {
      setExpanded('notifications', true);
    }
  };

  return (
    <div className="flex flex-none items-center border-grey30 border-b bg-white px-6 max-md:px-4">
      <Tabs value={tab} onChange={handleChange} className="min-h-12 min-w-0 flex-1">
        <Tab value="assistant" className={TAB_CLASS} label={isChecklistOpen ? 'Setup checklist' : 'AI assistant'} />
        <Tab
          value="notifications"
          className={TAB_CLASS}
          label={
            <span className="flex items-center gap-1.5">
              Notifications
              {unreadCount > 0 && (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-main px-1.5 text-white text-xs">
                  {unreadCount}
                </span>
              )}
            </span>
          }
        />
      </Tabs>
      {actions}
    </div>
  );
}
